// From Orchard CMS: https://github.com/OrchardCMS/OrchardCore/blob/af8921dfd61f399b455878ec8d761155aade6629/src/OrchardCore.Modules/OrchardCore.Liquid/Assets/monaco/liquid-intellisense.ts
// BSD 3-Clause License: https://github.com/OrchardCMS/OrchardCore/blob/af8921dfd61f399b455878ec8d761155aade6629/LICENSE
import type monaco from "monaco-editor";

interface ILiquidContextInfo {
  showFilters: boolean;
  showTags: boolean;
  showVariables: boolean;
  includeEndTags: boolean;
  inTag: boolean;
  inObject: boolean;
}

function getLiquidContextInfo(
  model: monaco.editor.ITextModel,
  position: monaco.Position,
  _triggerCharacter: string
): ILiquidContextInfo {
  let inTag = false;
  let inObject = false;
  let showTags = false;
  let showFilters = false;
  let showVariables = false;

  const findStart = model.findPreviousMatch(
    "\\{(%|\\{)",
    position,
    true,
    false,
    null,
    true
  );
  if (
    findStart &&
    findStart.matches &&
    !position.isBefore(findStart.range.getEndPosition())
  ) {
    if (findStart.matches[1] == "%") {
      inTag = true;
    } else if (findStart.matches[1] == "{") {
      inObject = true;
    }

    const searchPattern = inTag ? "%}" : "}}";
    const findEnd = model.findNextMatch(
      searchPattern,
      position,
      false,
      false,
      null,
      true
    );
    if (findEnd) {
      const currentRange = findStart.range.plusRange(findEnd.range);
      if (currentRange.containsPosition(position)) {
        if (inTag) {
          const findTagName = model.findNextMatch(
            "\\{%\\s*([a-zA-Z-_]+)",
            findStart.range.getStartPosition(),
            true,
            false,
            null,
            true
          );
          if (
            findTagName &&
            findTagName.matches &&
            currentRange.containsRange(findTagName.range) &&
            findTagName.matches.length > 1
          ) {
            if (["if", "else", "unless"].includes(findTagName.matches[1])) {
              showVariables = true;
            }

            showTags = false;
          } else {
            showTags = true;
          }
        } else {
          showVariables = true;
        }

        if (model.getValueInRange(currentRange).includes("|")) {
          showFilters = true;
        }
      }
    }
  }

  return {
    showFilters,
    showTags,
    showVariables,
    inTag,
    inObject,
  } as ILiquidContextInfo;
}

export function configureLiquidIntellisense(
  monacoInstance: any,
  {
    getVariables,
    filters,
    tags,
  }: { getVariables: () => string[]; filters: string[]; tags: string[] }
): { dispose: () => void } {
  const completionItemProvider: monaco.languages.CompletionItemProvider = {
    triggerCharacters: [" "],
    provideCompletionItems: (
      model: monaco.editor.ITextModel,
      position: monaco.Position,
      context: monaco.languages.CompletionContext,
      _token: monaco.CancellationToken
    ) => {
      let items: string[] = [];

      const liquidContext: ILiquidContextInfo = getLiquidContextInfo(
        model,
        position,
        context.triggerCharacter!
      );

      if (context.triggerCharacter == " ") {
        const startTrigger = model.getValueInRange(
          new monacoInstance.Range(
            position.lineNumber,
            position.column - 3,
            position.lineNumber,
            position.column - 1
          )
        );

        if (
          startTrigger != "{%" &&
          !startTrigger.endsWith("|") &&
          !liquidContext.inTag &&
          !liquidContext.inObject
        ) {
          return null;
        }
      }

      if (liquidContext.showFilters) {
        items = filters;
      } else if (liquidContext.showTags) {
        items = tags.filter((value: string) => {
          return !value.startsWith("end");
        });
      }

      if (liquidContext.showVariables) {
        items = items.concat(getVariables());
      }

      const suggestions = items.map((value: string) => {
        return {
          label: value,
          kind: monacoInstance.languages.CompletionItemKind.Keyword,
          insertText: value,
          insertTextRules:
            monacoInstance.languages.CompletionItemInsertTextRule
              .KeepWhitespace,
        } as monaco.languages.CompletionItem;
      });

      return {
        suggestions,
      } as monaco.languages.ProviderResult<monaco.languages.CompletionList>;
    },
  };

  return monacoInstance.languages.registerCompletionItemProvider(
    "liquid",
    completionItemProvider
  );
}
